/** QuickRouter — Empilages JLCPCB et calcul d'impédance (pistes simples et paires).
 *
 * Empilages : épaisseurs (mm) et εr d'après les tableaux d'empilage publiés par JLCPCB
 * (cuivre externe 1 oz = 0,035 mm, interne 0,5 oz = 0,0152 mm ; préimprégnés 7628 εr 4,4,
 * 2116 εr 4,16, 3313 εr 4,1 ; âmes εr 4,6). VALEURS À VÉRIFIER sur le calculateur d'impédance
 * JLCPCB avant commande : il fait foi, et la référence d'empilage doit être saisie à la commande.
 *
 * Formules (précision ±10 % — l'ordre de grandeur que garantit un fabricant sans essai) :
 *  - microstrip : Hammerstad-Jensen, épaisseur de cuivre par correction de Wheeler, sans vernis
 *    (le vernis abaisse Z0 de 1 à 3 Ω) ;
 *  - stripline symétrique : IPC-2141 (Z0 = 60/√εr · ln(4b / (0,67π(0,8w + t)))) ; asymétrique :
 *    combinaison « en parallèle » des deux striplines symétriques équivalentes ;
 *  - paires couplées par la tranche (IPC-2141) : microstrip Zd = 2Z0(1 − 0,48·e^(−0,96 s/h)) ;
 *    stripline Zd = 2Z0(1 − 0,347·e^(−2,9 s/b)).
 */
import { copperLayers } from './layers.js';

const ETA0 = 376.73;
const OZ1 = 0.035, OZ05 = 0.0152;

const cu = (name, t) => ({ type: 'copper', name, t });
const di = (t, er, material) => ({ type: 'dielectric', t, er, material });

/** Empilages proposés, du dessus vers le dessous. */
export const STACKUPS = {
  JLC02161: {
    name: '2 couches 1,6 mm (FR-4 standard)',
    layers: 2,
    stack: [cu('F', OZ1), di(1.53, 4.5, 'FR-4'), cu('B', OZ1)],
  },
  'JLC04161H-7628': {
    name: 'JLCPCB 4 couches 1,6 mm — JLC04161H-7628',
    layers: 4,
    stack: [cu('F', OZ1), di(0.2104, 4.4, '7628'), cu('In1', OZ05), di(1.065, 4.6, 'âme'), cu('In2', OZ05), di(0.2104, 4.4, '7628'), cu('B', OZ1)],
  },
  'JLC04161H-3313': {
    name: 'JLCPCB 4 couches 1,6 mm — JLC04161H-3313',
    layers: 4,
    stack: [cu('F', OZ1), di(0.0994, 4.1, '3313'), cu('In1', OZ05), di(1.265, 4.6, 'âme'), cu('In2', OZ05), di(0.0994, 4.1, '3313'), cu('B', OZ1)],
  },
  'JLC06161H-2116': {
    name: 'JLCPCB 6 couches 1,6 mm — JLC06161H-2116',
    layers: 6,
    stack: [
      cu('F', OZ1), di(0.1164, 4.16, '2116'), cu('In1', OZ05), di(0.55, 4.6, 'âme'), cu('In2', OZ05),
      di(0.1164, 4.16, '2116'), cu('In3', OZ05), di(0.55, 4.6, 'âme'), cu('In4', OZ05), di(0.1164, 4.16, '2116'), cu('B', OZ1),
    ],
  },
  'JLC06161H-3313': {
    name: 'JLCPCB 6 couches 1,6 mm — JLC06161H-3313',
    layers: 6,
    stack: [
      cu('F', OZ1), di(0.0994, 4.1, '3313'), cu('In1', OZ05), di(0.585, 4.6, 'âme'), cu('In2', OZ05),
      di(0.0994, 4.1, '3313'), cu('In3', OZ05), di(0.585, 4.6, 'âme'), cu('In4', OZ05), di(0.0994, 4.1, '3313'), cu('B', OZ1),
    ],
  },
};

export const DEFAULT_STACKUP = { 2: 'JLC02161', 4: 'JLC04161H-7628', 6: 'JLC06161H-2116' };

/** Empilage de la carte (celui choisi s'il correspond au nombre de couches, sinon le défaut). */
export function stackupOf(board) {
  const n = board?.layers ?? 2;
  const s = STACKUPS[board?.stackup];
  return s && s.layers === n ? { id: board.stackup, ...s } : { id: DEFAULT_STACKUP[n], ...STACKUPS[DEFAULT_STACKUP[n]] };
}

export const stackupsFor = (n) => Object.entries(STACKUPS).filter(([, s]) => s.layers === n).map(([id, s]) => ({ id, name: s.name }));

/** Géométrie vue d'une couche : microstrip (externe) ou stripline (interne, h1 au-dessus, h2 dessous). */
export function layerGeometry(board, L) {
  const st = stackupOf(board).stack;
  const k = st.findIndex((x) => x.type === 'copper' && x.name === L);
  if (k < 0) throw new Error(`couche ${L} absente de l'empilage`);
  const t = st[k].t;
  const above = k > 0 ? st[k - 1] : null, below = k < st.length - 1 ? st[k + 1] : null;
  if (!above || !below) {
    const d = above ?? below;
    return { kind: 'microstrip', t, h: d.t, er: d.er, ref: st[above ? k - 2 : k + 2]?.name };
  }
  return { kind: 'stripline', t, h1: above.t, h2: below.t, er: (above.er * above.t + below.er * below.t) / (above.t + below.t), refs: [st[k - 2]?.name, st[k + 2]?.name] };
}

// ── Formules ───────────────────────────────────────────────────

/** Microstrip, Hammerstad-Jensen avec épaisseur (Wheeler). */
export function microstripZ0(w, h, t, er) {
  const dw = t > 0 ? (t / Math.PI) * Math.log(1 + (4 * Math.E) / (t * (1 / Math.tanh(Math.sqrt(6.517 * w / h))) ** 2)) : 0;
  const dwr = (dw * (1 + 1 / Math.cosh(Math.sqrt(er - 1)))) / 2;
  const zAir = (u) => {
    const f = 6 + (2 * Math.PI - 6) * Math.exp(-((30.666 / u) ** 0.7528));
    return (ETA0 / (2 * Math.PI)) * Math.log(f / u + Math.sqrt(1 + (2 / u) ** 2));
  };
  const ur = (w + dwr) / h;
  const a = 1 + Math.log((ur ** 4 + (ur / 52) ** 2) / (ur ** 4 + 0.432)) / 49 + Math.log(1 + (ur / 18.1) ** 3) / 18.7;
  const b = 0.564 * ((er - 0.9) / (er + 3)) ** 0.053;
  const eeff = (er + 1) / 2 + ((er - 1) / 2) * (1 + 10 / ur) ** (-a * b);
  return zAir(ur) / Math.sqrt(eeff);
}

/** Stripline symétrique (IPC-2141), b = écart entre plans. */
function striplineSym(w, b, t, er) {
  // Hors du domaine de la formule (w/b > 0,35 environ), le logarithme s'effondre : on borne à
  // 1 Ω pour garder une fonction décroissante (la bissection ne quitte pas le domaine utile).
  return Math.max(1, (60 / Math.sqrt(er)) * Math.log((4 * b) / (0.67 * Math.PI * (0.8 * w + t))));
}

/** Stripline asymétrique : plans à h1 (au-dessus) et h2 (au-dessous) du cuivre. */
export function striplineZ0(w, h1, h2, t, er) {
  const za = striplineSym(w, 2 * h1 + t, t, er), zb = striplineSym(w, 2 * h2 + t, t, er);
  return (2 * za * zb) / (za + zb);
}

/** Impédance d'une piste (simple) ou d'une paire (s = écart bord à bord) sur une couche. */
export function impedance(board, L, w, s = null) {
  const g = layerGeometry(board, L);
  if (g.kind === 'microstrip') {
    const z0 = microstripZ0(w, g.h, g.t, g.er);
    return s == null ? z0 : 2 * z0 * (1 - 0.48 * Math.exp((-0.96 * s) / g.h));
  }
  const z0 = striplineZ0(w, g.h1, g.h2, g.t, g.er);
  const b = g.h1 + g.h2 + g.t;
  return s == null ? z0 : 2 * z0 * (1 - 0.347 * Math.exp((-2.9 * s) / b));
}

/** Largeur donnant l'impédance visée (bissection, Z décroît quand w croît). */
function solveWidth(f, target, lo = 0.05, hi = 3) {
  if (f(lo) < target) return lo;
  if (f(hi) > target) return hi;
  for (let k = 0; k < 60; k++) {
    const m = (lo + hi) / 2;
    if (f(m) > target) lo = m; else hi = m;
  }
  return (lo + hi) / 2;
}

const r3 = (v) => Math.round(v * 1000) / 1000;

/**
 * Synthèse : largeur (et écart pour une paire) pour l'impédance visée sur la couche.
 * Paire : écart `s` imposé, sinon écart = largeur (couplage serré usuel), borné par minGap.
 * Rend { w, s?, z, geometry }.
 */
export function synthesize(board, L, target, { diff = false, s = null, minGap = 0.1, minWidth = 0.09 } = {}) {
  const geometry = layerGeometry(board, L);
  if (!diff) {
    const w = Math.max(minWidth, solveWidth((x) => impedance(board, L, x), target));
    return { w: r3(w), z: Math.round(impedance(board, L, w) * 10) / 10, geometry };
  }
  let w = 0.2, gap = s ?? 0.2;
  for (let k = 0; k < 30; k++) {
    if (s == null) gap = Math.max(minGap, w);
    w = Math.max(minWidth, solveWidth((x) => impedance(board, L, x, gap), target));
  }
  if (s == null) gap = Math.max(minGap, w);
  return { w: r3(w), s: r3(gap), z: Math.round(impedance(board, L, w, gap) * 10) / 10, geometry };
}

/** Résumé lisible de l'empilage (LISEZMOI, assistant). */
export function stackupSummary(board) {
  const s = stackupOf(board);
  const lines = s.stack.map((x) => (x.type === 'copper' ? `${x.name} cuivre ${x.t} mm` : `   ${x.material} ${x.t} mm εr ${x.er}`));
  return { id: s.id, name: s.name, lines, layers: copperLayers(board) };
}
